import { tokens } from '@/lib/tokens.js';
import { useState } from 'react';
import {
  useAdminTenantTemplates,
  useToggleEnabled,
  useUninstallTenantTemplate,
} from '../../hooks/use-tenant-templates.js';

const s = {
  container: {
    backgroundColor: tokens.colors.bgCardDark,
    borderRadius: tokens.radius.card,
    overflow: 'hidden',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,.1)',
  },
  header: {
    padding: '1rem 1.5rem',
    borderBottom: `1px solid ${tokens.colors.borderDark}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: { fontSize: '1rem', fontWeight: 600, color: tokens.colors.textDark, margin: 0 },
  table: { width: '100%', borderCollapse: 'collapse' as const },
  th: {
    padding: '0.75rem 1rem',
    textAlign: 'left' as const,
    fontSize: '0.75rem',
    fontWeight: 600,
    color: tokens.colors.textMutedDark,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    borderBottom: `1px solid ${tokens.colors.borderDark}`,
    backgroundColor: tokens.colors.bgCardDark,
  },
  td: {
    padding: '0.75rem 1rem',
    fontSize: '0.875rem',
    color: tokens.colors.textDark,
    borderBottom: '1px solid rgba(55,65,81,0.5)',
    verticalAlign: 'middle' as const,
  },
  trOdd: { backgroundColor: 'rgba(17,24,39,0.3)' },
  btn: {
    backgroundColor: tokens.colors.primary,
    color: '#fff',
    border: 'none',
    borderRadius: tokens.radius.button,
    padding: '0.375rem 0.875rem',
    fontSize: '0.875rem',
    cursor: 'pointer',
    fontWeight: 500,
  },
  btnSm: {
    backgroundColor: 'transparent',
    border: `1px solid ${tokens.colors.borderDark}`,
    borderRadius: tokens.radius.button,
    color: tokens.colors.textMutedDark,
    padding: '0.25rem 0.5rem',
    fontSize: '0.75rem',
    cursor: 'pointer',
    marginRight: '0.25rem',
  },
  btnDanger: {
    backgroundColor: 'transparent',
    border: `1px solid ${tokens.colors.error}`,
    borderRadius: tokens.radius.button,
    color: tokens.colors.error,
    padding: '0.25rem 0.5rem',
    fontSize: '0.75rem',
    cursor: 'pointer',
  },
  toggleOn: {
    backgroundColor: '#166534',
    border: 'none',
    borderRadius: '9999px',
    color: '#bbf7d0',
    padding: '0.125rem 0.5rem',
    fontSize: '0.75rem',
    cursor: 'pointer',
  },
  toggleOff: {
    backgroundColor: '#374151',
    border: 'none',
    borderRadius: '9999px',
    color: '#9ca3af',
    padding: '0.125rem 0.5rem',
    fontSize: '0.75rem',
    cursor: 'pointer',
  },
};

interface TenantTemplatesListProps {
  tenantId: string;
  onInstallClick?: () => void;
  onMigrateClick?: (tenantReportId: string) => void;
}

export function TenantTemplatesList({
  tenantId,
  onInstallClick,
  onMigrateClick,
}: TenantTemplatesListProps) {
  // B-4: use the tenant-scoped hook (GET /admin/tenants/:tenantId/templates) instead of global catalog
  const { data, isLoading } = useAdminTenantTemplates(tenantId, { 'page-size': 50 });
  const toggle = useToggleEnabled(tenantId);
  const uninstall = useUninstallTenantTemplate(tenantId);
  const [toggled, setToggled] = useState<Record<string, boolean>>({});

  // B-4: use tenant_report_id (the tenant_report row PK) — not the global template id
  function handleToggle(tenantReportId: string, current: boolean) {
    setToggled((prev) => ({ ...prev, [tenantReportId]: !current }));
    toggle.mutate({ tenantReportId, body: { enabled: !current } });
  }

  function handleUninstall(tenantReportId: string) {
    if (!window.confirm('Uninstall this template from the tenant?')) return;
    uninstall.mutate(tenantReportId);
  }

  const items = data?.items ?? [];

  return (
    <div style={s.container}>
      <div style={s.header}>
        <h2 style={s.title}>Tenant Installations</h2>
        <button type="button" style={s.btn} onClick={onInstallClick} aria-label="Install template">
          + Install
        </button>
      </div>
      {isLoading ? (
        <output
          style={{
            display: 'block',
            padding: '2rem',
            textAlign: 'center',
            color: tokens.colors.textMutedDark,
          }}
          aria-label="Loading installations"
        >
          Loading…
        </output>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={s.table} aria-label="Tenant templates list">
            <thead>
              <tr>
                <th style={s.th}>Code</th>
                <th style={s.th}>Version</th>
                <th style={s.th}>Kind</th>
                <th style={s.th}>Category</th>
                <th style={s.th}>Enabled</th>
                <th style={s.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    style={{ ...s.td, textAlign: 'center', color: tokens.colors.textMutedDark }}
                  >
                    No templates installed
                  </td>
                </tr>
              ) : (
                items.map((item, idx) => {
                  // Use tenant_report_id for all mutations; local toggled state overrides server value
                  const isEnabled = toggled[item.tenant_report_id] ?? item.enabled;
                  return (
                    <tr key={item.tenant_report_id} style={idx % 2 === 1 ? s.trOdd : undefined}>
                      <td style={{ ...s.td, fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                        {item.code}
                      </td>
                      <td style={s.td}>v{item.version}</td>
                      <td style={s.td}>{item.kind}</td>
                      <td style={s.td}>{item.category}</td>
                      <td style={s.td}>
                        <button
                          type="button"
                          style={isEnabled ? s.toggleOn : s.toggleOff}
                          onClick={() => handleToggle(item.tenant_report_id, isEnabled)}
                          aria-label={isEnabled ? 'Disable template' : 'Enable template'}
                        >
                          {isEnabled ? 'Enabled' : 'Disabled'}
                        </button>
                      </td>
                      <td style={s.td}>
                        <button
                          type="button"
                          style={s.btnSm}
                          onClick={() => onMigrateClick?.(item.tenant_report_id)}
                          aria-label="Migrate version"
                        >
                          Migrate
                        </button>
                        <button
                          type="button"
                          style={s.btnDanger}
                          onClick={() => handleUninstall(item.tenant_report_id)}
                          aria-label="Uninstall template"
                        >
                          Uninstall
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
